import React, { useEffect, useState } from "react";
import { ExternalLink, ChevronLeft, ChevronRight, Download } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toCsv, downloadCsv, stamp, fetchAllRows } from "@/lib/csv";
import { SUB_COLS, INV_COLS, EMAIL_COLS, TC_COLS } from "@/lib/analyticsReport";

const TIER_LABELS = { standard: "VIP", pro: "PRO", premium: "CAPITAL", business: "TEAMS" };
const fmtUnix = (ts) => (ts ? new Date(ts * 1000).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtISO = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtISOTime = (s) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

const STATUS_CLS = {
  active: "bg-teal-50 text-teal-700", trialing: "bg-teal-50 text-teal-700", paid: "bg-teal-50 text-teal-700",
  past_due: "bg-amber-50 text-amber-700", open: "bg-amber-50 text-amber-700",
  canceled: "bg-secondary text-muted-foreground", void: "bg-secondary text-muted-foreground",
  uncollectible: "bg-red-50 text-red-700",
};
const StatusBadge = ({ s }) => <Badge className={`${STATUS_CLS[s] || "bg-secondary text-muted-foreground"} text-[10px]`}>{s || "—"}</Badge>;

function FilterSelect({ value, onChange, options, placeholder, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-[150px]" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Pager({ page, total, pageSize, onPage }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
      <span>{total} record{total === 1 ? "" : "s"}</span>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="dataset-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <span>Page {page} / {pages}</span>
        <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="dataset-next"><ChevronRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => <th className={`px-3 py-2 text-left text-[11px] font-[640] uppercase tracking-wide text-muted-foreground ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2.5 ${className}`}>{children}</td>;

function ExportButton({ onExport, testId }) {
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); try { await onExport(); } finally { setBusy(false); } };
  return (
    <Button size="sm" variant="secondary" className="ml-auto" onClick={run} disabled={busy} data-testid={testId}>
      <Download className="mr-1.5 h-3.5 w-3.5" /> {busy ? "Exporting…" : "Export CSV"}
    </Button>
  );
}

function TableWrap({ children, empty, rows, testId }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border" data-testid={testId}>
      <table className="w-full text-sm">{children}</table>
      {rows === 0 && <div className="px-3 py-8 text-center text-sm text-muted-foreground">{empty}</div>}
    </div>
  );
}

/* ----------------------------------------------------------- Subscribers */
function Subscribers({ from, to }) {
  const [tier, setTier] = useState("all");
  const [status, setStatus] = useState("active");
  const [interval, setInterval] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0, page_size: 25 });

  useEffect(() => {
    const p = new URLSearchParams({ page: String(page), page_size: "25" });
    if (tier !== "all") p.set("tier", tier);
    if (status !== "all") p.set("status", status);
    if (interval !== "all") p.set("interval", interval);
    api.get(`/admin/analytics/subscribers?${p}`).then(({ data }) => setData(data)).catch(() => {});
  }, [tier, status, interval, page]);
  useEffect(() => setPage(1), [tier, status, interval]);

  const exportCsv = async () => {
    const params = {};
    if (tier !== "all") params.tier = tier;
    if (status !== "all") params.status = status;
    if (interval !== "all") params.interval = interval;
    const rows = await fetchAllRows(api, "/admin/analytics/subscribers", params);
    downloadCsv(`subscribers_${stamp()}.csv`, toCsv(SUB_COLS, rows));
  };

  return (
    <div data-testid="dataset-subscribers">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <FilterSelect value={tier} onChange={setTier} testId="sub-filter-tier" options={[{ value: "all", label: "All tiers" }, { value: "pro", label: "PRO" }, { value: "premium", label: "CAPITAL" }, { value: "standard", label: "VIP" }, { value: "business", label: "TEAMS" }]} />
        <FilterSelect value={status} onChange={setStatus} testId="sub-filter-status" options={[{ value: "active", label: "Active" }, { value: "all", label: "Active (default)" }, { value: "trialing", label: "Trialing" }, { value: "past_due", label: "Past due" }, { value: "canceled", label: "Canceled" }]} />
        <FilterSelect value={interval} onChange={setInterval} testId="sub-filter-interval" options={[{ value: "all", label: "All billing" }, { value: "month", label: "Monthly" }, { value: "year", label: "Annual" }]} />
        <ExportButton onExport={exportCsv} testId="sub-export-csv" />
      </div>
      <TableWrap rows={data.rows.length} empty="No subscribers match these filters." testId="sub-table">
        <thead className="bg-secondary/50"><tr><Th>Member</Th><Th>Tier</Th><Th>Billing</Th><Th>Status</Th><Th>Renews/Cancels</Th><Th>Joined</Th></tr></thead>
        <tbody className="divide-y divide-border/70">
          {data.rows.map((r) => (
            <tr key={r.user_id} data-testid={`sub-row-${r.user_id}`}>
              <Td><div className="font-[560] text-foreground">{r.email}</div><div className="text-xs text-muted-foreground">{`${r.first_name || ""} ${r.last_name || ""}`.trim()}</div></Td>
              <Td>{TIER_LABELS[r.tier] || r.tier}</Td>
              <Td className="capitalize">{r.subscription_interval === "year" ? "Annual" : r.subscription_interval === "month" ? "Monthly" : "—"}</Td>
              <Td><StatusBadge s={r.subscription_status} /></Td>
              <Td className={r.subscription_cancel_at_period_end ? "text-amber-700" : ""}>{fmtUnix(r.subscription_current_period_end)}{r.subscription_cancel_at_period_end ? " (cancels)" : ""}</Td>
              <Td className="text-muted-foreground">{fmtISO(r.created_at)}</Td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      <Pager page={data.page} total={data.total} pageSize={data.page_size} onPage={setPage} />
    </div>
  );
}

/* ----------------------------------------------------------- Invoices (Stripe) */
function Invoices() {
  const [status, setStatus] = useState("all");
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [configured, setConfigured] = useState(true);

  const fetchPage = (reset, after) => {
    const p = new URLSearchParams({ limit: "25" });
    if (status !== "all") p.set("status", status);
    if (after) p.set("starting_after", after);
    api.get(`/admin/analytics/invoices?${p}`).then(({ data }) => {
      setConfigured(data.configured);
      setRows((prev) => (reset ? data.rows : [...prev, ...data.rows]));
      setHasMore(data.has_more);
      setCursor(data.next_cursor);
    }).catch(() => {});
  };
  useEffect(() => { fetchPage(true, null); /* eslint-disable-next-line */ }, [status]);

  const exportCsv = async () => {
    const params = {};
    if (status !== "all") params.status = status;
    const all = await fetchAllRows(api, "/admin/analytics/invoices", params, { cursor: true });
    downloadCsv(`invoices_${stamp()}.csv`, toCsv(INV_COLS, all));
  };

  return (
    <div data-testid="dataset-invoices">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <FilterSelect value={status} onChange={setStatus} testId="inv-filter-status" options={[{ value: "all", label: "All statuses" }, { value: "paid", label: "Paid" }, { value: "open", label: "Open" }, { value: "uncollectible", label: "Failed" }, { value: "void", label: "Void" }]} />
        <ExportButton onExport={exportCsv} testId="inv-export-csv" />
      </div>
      {!configured ? (
        <p className="text-sm text-muted-foreground">Stripe isn&apos;t configured.</p>
      ) : (
        <>
          <TableWrap rows={rows.length} empty="No invoices match these filters." testId="inv-table">
            <thead className="bg-secondary/50"><tr><Th>Date</Th><Th>Member</Th><Th>Amount</Th><Th>Status</Th><Th>Period</Th><Th>Links</Th></tr></thead>
            <tbody className="divide-y divide-border/70">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`inv-row-${r.id}`}>
                  <Td className="whitespace-nowrap">{fmtUnix(r.created)}</Td>
                  <Td className="text-foreground">{r.email || "—"}</Td>
                  <Td className="font-[600]">{r.currency} {Number(r.amount).toFixed(2)}</Td>
                  <Td><StatusBadge s={r.status} /></Td>
                  <Td className="whitespace-nowrap text-xs text-muted-foreground">{fmtUnix(r.period_start)} – {fmtUnix(r.period_end)}</Td>
                  <Td>{r.hosted_invoice_url && <a href={r.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-primary hover:underline" data-testid={`inv-link-${r.id}`}><ExternalLink className="h-3.5 w-3.5" /></a>}</Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          {hasMore && <Button size="sm" variant="secondary" className="mt-3" onClick={() => fetchPage(false, cursor)} data-testid="inv-load-more">Load more</Button>}
        </>
      )}
    </div>
  );
}

/* ----------------------------------------------------------- Emails */
function Emails({ from, to }) {
  const [mode, setMode] = useState("all");
  const [tag, setTag] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0, page_size: 25 });

  useEffect(() => {
    const p = new URLSearchParams({ page: String(page), page_size: "25" });
    if (mode !== "all") p.set("mode", mode);
    if (tag.trim()) p.set("tag", tag.trim());
    api.get(`/admin/analytics/emails?${p}`).then(({ data }) => setData(data)).catch(() => {});
  }, [mode, tag, page]);
  useEffect(() => setPage(1), [mode, tag]);

  const exportCsv = async () => {
    const params = {};
    if (mode !== "all") params.mode = mode;
    if (tag.trim()) params.tag = tag.trim();
    const rows = await fetchAllRows(api, "/admin/analytics/emails", params);
    downloadCsv(`emails_${stamp()}.csv`, toCsv(EMAIL_COLS, rows));
  };

  return (
    <div data-testid="dataset-emails">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <FilterSelect value={mode} onChange={setMode} testId="email-filter-mode" options={[{ value: "all", label: "All modes" }, { value: "mailersend", label: "Delivered" }, { value: "log", label: "Log only" }]} />
        <Input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="Filter by tag…" className="h-9 w-[180px]" data-testid="email-filter-tag" />
        <ExportButton onExport={exportCsv} testId="email-export-csv" />
      </div>
      <TableWrap rows={data.rows.length} empty="No emails match these filters." testId="email-table">
        <thead className="bg-secondary/50"><tr><Th>Date</Th><Th>To</Th><Th>Subject</Th><Th>Tag</Th><Th>Mode</Th></tr></thead>
        <tbody className="divide-y divide-border/70">
          {data.rows.map((r) => (
            <tr key={r.id} data-testid={`email-row-${r.id}`}>
              <Td className="whitespace-nowrap text-muted-foreground">{fmtISOTime(r.created_at)}</Td>
              <Td className="text-foreground">{r.to}</Td>
              <Td className="max-w-[280px] truncate">{r.subject}</Td>
              <Td>{r.tag && <Badge variant="secondary" className="text-[10px]">{r.tag}</Badge>}</Td>
              <Td><Badge className={`${r.mode === "mailersend" ? "bg-teal-50 text-teal-700" : "bg-amber-50 text-amber-700"} text-[10px]`}>{r.mode === "mailersend" ? "Delivered" : "Log"}</Badge></Td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      <Pager page={data.page} total={data.total} pageSize={data.page_size} onPage={setPage} />
    </div>
  );
}

/* ----------------------------------------------------------- Tier changes */
function TierChanges({ from, to }) {
  const [direction, setDirection] = useState("all");
  const [source, setSource] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0, page_size: 25 });

  useEffect(() => {
    const p = new URLSearchParams({ page: String(page), page_size: "25" });
    if (direction !== "all") p.set("direction", direction);
    if (source !== "all") p.set("source", source);
    api.get(`/admin/analytics/tier-changes?${p}`).then(({ data }) => setData(data)).catch(() => {});
  }, [direction, source, page]);
  useEffect(() => setPage(1), [direction, source]);

  const exportCsv = async () => {
    const params = {};
    if (direction !== "all") params.direction = direction;
    if (source !== "all") params.source = source;
    const rows = await fetchAllRows(api, "/admin/analytics/tier-changes", params);
    downloadCsv(`tier-changes_${stamp()}.csv`, toCsv(TC_COLS, rows));
  };

  return (
    <div data-testid="dataset-tc">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <FilterSelect value={direction} onChange={setDirection} testId="tc-filter-direction" options={[{ value: "all", label: "All directions" }, { value: "upgrade", label: "Upgrades" }, { value: "downgrade", label: "Downgrades" }]} />
        <FilterSelect value={source} onChange={setSource} testId="tc-filter-source" options={[{ value: "all", label: "All sources" }, { value: "stripe_subscription", label: "Stripe subscription" }, { value: "stripe_subscription_ended", label: "Subscription ended" }, { value: "admin", label: "Admin" }, { value: "redeem", label: "License redeem" }, { value: "founder", label: "Founder" }]} />
        <ExportButton onExport={exportCsv} testId="tc-export-csv" />
      </div>
      <TableWrap rows={data.rows.length} empty="No tier changes match these filters." testId="tc-table">
        <thead className="bg-secondary/50"><tr><Th>Date</Th><Th>Member</Th><Th>Change</Th><Th>Direction</Th><Th>Source</Th></tr></thead>
        <tbody className="divide-y divide-border/70">
          {data.rows.map((r) => (
            <tr key={r.id} data-testid={`tc-row-${r.id}`}>
              <Td className="whitespace-nowrap text-muted-foreground">{fmtISOTime(r.created_at)}</Td>
              <Td className="text-foreground">{r.email || r.user_id}</Td>
              <Td>{TIER_LABELS[r.from_tier] || r.from_tier} → <span className="font-[600]">{TIER_LABELS[r.to_tier] || r.to_tier}</span></Td>
              <Td><Badge className={`${r.direction === "upgrade" ? "bg-teal-50 text-teal-700" : "bg-amber-50 text-amber-700"} text-[10px]`}>{r.direction}</Badge></Td>
              <Td className="text-xs text-muted-foreground">{r.source}</Td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      <Pager page={data.page} total={data.total} pageSize={data.page_size} onPage={setPage} />
    </div>
  );
}

/* ----------------------------------------------------------- Donations */
const DONATION_COLS = [
  { label: "Date", value: (r) => r.paid_at || r.created_at },
  { label: "Donor", value: (r) => r.donor_name || "" },
  { label: "Email", value: (r) => r.donor_email || "" },
  { label: "Amount", value: (r) => Number(r.amount || 0).toFixed(2) },
  { label: "Currency", value: (r) => (r.currency || "usd").toUpperCase() },
  { label: "Frequency", value: (r) => (r.frequency === "monthly" ? "Monthly" : "One-time") },
  { label: "Entry", value: (r) => r.entry_type },
  { label: "Status", value: (r) => r.status },
  { label: "Member id", value: (r) => r.user_id || "" },
  { label: "Note", value: (r) => r.message || "" },
];

function Donations({ from, to }) {
  const [status, setStatus] = useState("paid");
  const [frequency, setFrequency] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0, page_size: 25 });

  useEffect(() => {
    const p = new URLSearchParams({ page: String(page), page_size: "25" });
    if (status !== "all") p.set("status", status);
    if (frequency !== "all") p.set("frequency", frequency);
    api.get(`/admin/donations?${p}`).then(({ data }) => setData(data)).catch(() => {});
  }, [status, frequency, page]);
  useEffect(() => setPage(1), [status, frequency]);

  const exportCsv = async () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (frequency !== "all") params.frequency = frequency;
    const rows = await fetchAllRows(api, "/admin/donations", params);
    downloadCsv(`donations_${stamp()}.csv`, toCsv(DONATION_COLS, rows));
  };

  return (
    <div data-testid="dataset-donations">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <FilterSelect value={status} onChange={setStatus} testId="donation-filter-status" options={[{ value: "paid", label: "Paid" }, { value: "all", label: "All statuses" }, { value: "initiated", label: "Started" }, { value: "expired", label: "Abandoned" }]} />
        <FilterSelect value={frequency} onChange={setFrequency} testId="donation-filter-frequency" options={[{ value: "all", label: "One-time + monthly" }, { value: "one_time", label: "One-time" }, { value: "monthly", label: "Monthly" }]} />
        <ExportButton onExport={exportCsv} testId="donation-export-csv" />
      </div>
      <TableWrap rows={data.rows.length} empty="No donations match these filters." testId="donation-table">
        <thead className="bg-secondary/50"><tr><Th>Date</Th><Th>Donor</Th><Th>Amount</Th><Th>Type</Th><Th>Status</Th><Th>Note</Th></tr></thead>
        <tbody className="divide-y divide-border/70">
          {data.rows.map((r) => (
            <tr key={r.id} data-testid={`donation-row-${r.id}`}>
              <Td className="whitespace-nowrap text-muted-foreground">{fmtISOTime(r.paid_at || r.created_at)}</Td>
              <Td><div className="font-[560] text-foreground">{r.donor_name || "—"}</div><div className="text-xs text-muted-foreground">{r.donor_email || "—"}</div></Td>
              <Td className="font-[600]">{(r.currency || "usd").toUpperCase()} {Number(r.amount || 0).toFixed(2)}</Td>
              <Td>
                <Badge className={`${r.frequency === "monthly" ? "bg-teal-50 text-teal-700" : "bg-secondary text-muted-foreground"} text-[10px]`}>
                  {r.frequency === "monthly" ? (r.entry_type === "renewal" ? "Monthly · renewal" : "Monthly") : "One-time"}
                </Badge>
              </Td>
              <Td><StatusBadge s={r.status} /></Td>
              <Td className="max-w-[220px] truncate text-xs text-muted-foreground">{r.message || "—"}</Td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      <Pager page={data.page} total={data.total} pageSize={data.page_size} onPage={setPage} />
    </div>
  );
}

export default function AnalyticsDatasets({ from, to }) {
  return (
    <Card className="p-4">
      <Tabs defaultValue="subscribers">
        <TabsList data-testid="datasets-tabs">
          <TabsTrigger value="subscribers" data-testid="datasets-tab-subscribers">Subscribers</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="datasets-tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="donations" data-testid="datasets-tab-donations">Donations</TabsTrigger>
          <TabsTrigger value="emails" data-testid="datasets-tab-emails">Emails</TabsTrigger>
          <TabsTrigger value="tier-changes" data-testid="datasets-tab-tier-changes">Tier changes</TabsTrigger>
        </TabsList>
        <TabsContent value="subscribers" className="mt-4"><Subscribers from={from} to={to} /></TabsContent>
        <TabsContent value="invoices" className="mt-4"><Invoices /></TabsContent>
        <TabsContent value="donations" className="mt-4"><Donations from={from} to={to} /></TabsContent>
        <TabsContent value="emails" className="mt-4"><Emails from={from} to={to} /></TabsContent>
        <TabsContent value="tier-changes" className="mt-4"><TierChanges from={from} to={to} /></TabsContent>
      </Tabs>
    </Card>
  );
}
